import type { LocalDate, LocalDateTime, LocalTime } from '../util/datetime';
import { dateTimeOf, daysBetween, dateOf, isBefore, minutesBetween } from '../util/datetime';
import { isOpenOnDate, type Restaurant } from '../model/restaurant';
import { PhoneNormalizer } from '../util/phoneNormalizer';
import { BookingField, type FieldError, type FieldErrors } from './fieldError';

/**
 * Reglas de validación del flujo de reserva (§12 del análisis).
 *
 * Sin estado y sin dependencias de React: recibe `now` como parámetro en lugar de leer el reloj
 * del sistema, de modo que las pruebas son deterministas.
 */
export const MAX_ADVANCE_DAYS = 90;
export const MIN_LEAD_MINUTES = 30;
export const MIN_PARTY = 1;
export const MAX_PARTY_ONLINE = 12;
export const MAX_PARTY_ABSOLUTE = 20;
export const MAX_NOTES_LENGTH = 500;

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export const BookingValidator = {
  validateDate(date: LocalDate, today: LocalDate): FieldError | null {
    if (isBefore(date, today)) return 'DATE_PAST';
    if (daysBetween(today, date) > MAX_ADVANCE_DAYS) return 'DATE_TOO_FAR';
    return null;
  },

  /**
   * @param restaurant si se conoce, se comprueba además que el local esté abierto a esa hora.
   *   Cuando el restaurante no publica horarios no se inventa un rechazo: la disponibilidad
   *   real la decide el servidor.
   */
  validateTime(
    date: LocalDate,
    time: LocalTime,
    now: LocalDateTime,
    restaurant: Restaurant | null = null,
  ): FieldError | null {
    if (minutesBetween(now, dateTimeOf(date, time)) < MIN_LEAD_MINUTES) return 'TIME_PAST';
    if (restaurant && restaurant.openingHours.length > 0 && !isOpenOnDate(restaurant, date, time)) {
      return 'TIME_CLOSED';
    }
    return null;
  },

  validatePartySize(size: number): FieldError | null {
    if (size < MIN_PARTY) return 'PARTY_MIN';
    if (size > MAX_PARTY_ONLINE) return 'PARTY_MAX';
    return null;
  },

  validateName(raw: string): FieldError | null {
    const name = raw.trim();
    if (name.length === 0) return 'NAME_REQUIRED';
    if (name.length < 2 || name.length > 60) return 'NAME_LENGTH';
    return null;
  },

  validatePhone(raw: string): FieldError | null {
    if (raw.trim().length === 0) return 'PHONE_REQUIRED';
    if (!PhoneNormalizer.isValid(raw)) return 'PHONE_FORMAT';
    return null;
  },

  validateEmail(raw: string): FieldError | null {
    const email = raw.trim();
    if (email.length === 0) return 'EMAIL_REQUIRED';
    if (!EMAIL_RE.test(email)) return 'EMAIL_FORMAT';
    return null;
  },

  validateNotes(raw: string | null | undefined): FieldError | null {
    return (raw?.length ?? 0) > MAX_NOTES_LENGTH ? 'NOTES_LENGTH' : null;
  },

  validateTerms(accepted: boolean): FieldError | null {
    return accepted ? null : 'TERMS_REQUIRED';
  },

  /** Valida el paso "fecha · hora · personas". */
  validateSlotStep(
    date: LocalDate,
    time: LocalTime,
    partySize: number,
    now: LocalDateTime,
    restaurant: Restaurant | null = null,
  ): FieldErrors {
    const errors: FieldErrors = {};
    const dateError = BookingValidator.validateDate(date, dateOf(now));
    if (dateError) errors[BookingField.DATE] = dateError;
    // La hora solo se evalúa si la fecha es válida: si la fecha ya está mal, añadir un segundo
    // error sobre la hora confunde en lugar de ayudar.
    if (!dateError) {
      const timeError = BookingValidator.validateTime(date, time, now, restaurant);
      if (timeError) errors[BookingField.TIME] = timeError;
    }
    const partyError = BookingValidator.validatePartySize(partySize);
    if (partyError) errors[BookingField.PARTY_SIZE] = partyError;
    return errors;
  },

  /** Valida el paso "tus datos". */
  validateGuestStep(name: string, phone: string, email: string, notes: string | null): FieldErrors {
    const errors: FieldErrors = {};
    const nameError = BookingValidator.validateName(name);
    if (nameError) errors[BookingField.NAME] = nameError;
    const phoneError = BookingValidator.validatePhone(phone);
    if (phoneError) errors[BookingField.PHONE] = phoneError;
    const emailError = BookingValidator.validateEmail(email);
    if (emailError) errors[BookingField.EMAIL] = emailError;
    const notesError = BookingValidator.validateNotes(notes);
    if (notesError) errors[BookingField.NOTES] = notesError;
    return errors;
  },

  /** Valida el paso final antes de enviar. */
  validateReviewStep(termsAccepted: boolean): FieldErrors {
    const errors: FieldErrors = {};
    const termsError = BookingValidator.validateTerms(termsAccepted);
    if (termsError) errors[BookingField.TERMS] = termsError;
    return errors;
  },
};
